import React, { useState } from 'react';
import { X, Mail, Send, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import Swal from 'sweetalert2';
import { sendEmail, sendTestQuotaEmail } from '../../../../../services/mailService';
import { ActionLoader } from '../../../../loader';

const EmailTestModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('normal');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Estados para email normal
  const [normalEmail, setNormalEmail] = useState({
    to: '',
    subject: '',
    message: '',
    cc: '',
    bcc: '',
    type: 'correo-generico'
  });

  // Estados para cuotas sociales
  const [quotaEmail, setQuotaEmail] = useState({
    to: '',
    subject: '',
    companyName: '',
    periodicity: '',
    month: '',
    amount: '',
    numberDocument: '',
    cc: '',
    bcc: ''
  });

  const resetForm = () => {
    setNormalEmail({
      to: '',
      subject: '',
      message: '',
      cc: '',
      bcc: '',
      type: 'correo-generico'
    });
    setQuotaEmail({
      to: '',
      subject: '',
      companyName: '',
      periodicity: '',
      month: '',
      amount: '',
      numberDocument: '',
    });
    setResult(null);
  };

  // Función para manejar el input del monto (solo números, no empezar con 0)
  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Solo permitir números (remover cualquier carácter que no sea dígito)
    let numericValue = value.replace(/[^0-9]/g, '');
    // No permitir que empiece con 0 (excepto si está vacío)
    if (numericValue.length > 1 && numericValue.startsWith('0')) {
      numericValue = numericValue.substring(1);
    }
    setQuotaEmail({...quotaEmail, amount: numericValue});
  };

  const handleSendNormalEmail = async () => {
    // Obtener destinatarios para la confirmación
    const recipients = normalEmail.to.split(',').map(email => email.trim()).filter(email => email);
    const recipientDisplay = recipients.length > 0 ? recipients.join(', ') : normalEmail.to.trim();
    
    // Confirmar antes de enviar
    const confirmResult = await Swal.fire({
      title: '¿Estás seguro?',
      html: `¿Estás seguro de enviar el correo hacia <strong>${recipientDisplay}</strong>?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0488AF',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, enviar',
      cancelButtonText: 'Cancelar',
    });

    if (!confirmResult.isConfirmed) {
      return; // Usuario canceló
    }

    setIsLoading(true);
    setResult(null);
    
    try {
      // Convertir strings de emails a arrays y filtrar vacíos
      const payload = {
        ...normalEmail,
        to: recipients,
        cc: normalEmail.cc ? normalEmail.cc.split(',').map(email => email.trim()).filter(email => email) : [],
        bcc: normalEmail.bcc ? normalEmail.bcc.split(',').map(email => email.trim()).filter(email => email) : []
      };
      setIsSubmitting(true);
      const response = await sendEmail(payload);
      setIsSubmitting(false);
      setResult({
        success: response.success,
        message: response.message
      });
    } catch (error) {
      setResult({
        success: false,
        message: 'Error de conexión: ' + error.message
      });
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleSendQuotaEmail = async () => {
    // Confirmar antes de enviar
    const confirmResult = await Swal.fire({
      title: '¿Estás seguro?',
      html: `¿Estás seguro de enviar el correo hacia <strong>${quotaEmail.to.trim()}</strong>?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0488AF',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, enviar',
      cancelButtonText: 'Cancelar',
    });

    if (!confirmResult.isConfirmed) {
      return; // Usuario canceló
    }

    setIsLoading(true);
    setResult(null);
    
    try {
      // Convertir strings de emails a arrays y filtrar vacíos
      const payload = {
        ...quotaEmail,
        amount: parseInt(quotaEmail.amount) || 0, // Convertir a entero
        to: [quotaEmail.to.trim()], // Solo un destinatario, convertir a array
      };
      setIsSubmitting(true);
      const response = await sendTestQuotaEmail(payload);
      setResult({
        success: response.success,
        message: response.message
      });
    
    } catch (error) {
      setResult({
        success: false,
        message: 'Error de conexión: ' + error.message
      });
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <ActionLoader open={isSubmitting} onClose={() => setIsSubmitting(false)}></ActionLoader>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Prueba de Envío de Correos
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('normal')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'normal'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Send className="w-4 h-4" />
              Email Normal
            </div>
          </button>
          <button
            onClick={() => setActiveTab('quota')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'quota'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <FileText className="w-4 h-4" />
              Cuotas Sociales
            </div>
          </button>
        </div>

        <div className="p-6">
          {/* Tab Content - Email Normal */}
          {activeTab === 'normal' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Para (To) *
                </label>
                <input
                  type="text"
                  value={normalEmail.to}
                  onChange={(e) => setNormalEmail({...normalEmail, to: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="destinatario@ejemplo.com, otro@ejemplo.com"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Separar múltiples emails con comas</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Asunto *
                </label>
                <input
                  type="text"
                  value={normalEmail.subject}
                  onChange={(e) => setNormalEmail({...normalEmail, subject: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Asunto del correo"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mensaje *
                </label>
                <textarea
                  value={normalEmail.message}
                  onChange={(e) => setNormalEmail({...normalEmail, message: e.target.value})}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Contenido del mensaje..."
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CC (Opcional)
                  </label>
                  <input
                    type="text"
                    value={normalEmail.cc}
                    onChange={(e) => setNormalEmail({...normalEmail, cc: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="cc@ejemplo.com, cc2@ejemplo.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">Separar con comas</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    BCC (Opcional)
                  </label>
                  <input
                    type="text"
                    value={normalEmail.bcc}
                    onChange={(e) => setNormalEmail({...normalEmail, bcc: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="bcc@ejemplo.com, bcc2@ejemplo.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">Separar con comas</p>
                </div>
              </div>
            </div>
          )}

          {/* Tab Content - Cuotas Sociales */}
          {activeTab === 'quota' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Para (To) *
                </label>
                <input
                  type="email"
                  value={quotaEmail.to}
                  onChange={(e) => setQuotaEmail({...quotaEmail, to: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="destinatario@ejemplo.com"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Un solo destinatario</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Asunto *
                </label>
                <input
                  type="text"
                  value={quotaEmail.subject}
                  onChange={(e) => setQuotaEmail({...quotaEmail, subject: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Asunto del correo"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de la Empresa *
                </label>
                <input
                  type="text"
                  value={quotaEmail.companyName}
                  onChange={(e) => setQuotaEmail({...quotaEmail, companyName: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Nombre de la empresa"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Periodicidad *
                  </label>
                  <select
                    value={quotaEmail.periodicity}
                    onChange={(e) => setQuotaEmail({...quotaEmail, periodicity: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Seleccionar</option>
                    <option value="Mensual">Mensual</option>
                    <option value="Semestral">Semestral</option>
                    <option value="Anual">Anual</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mes *
                  </label>
                  <select
                    value={quotaEmail.month}
                    onChange={(e) => setQuotaEmail({...quotaEmail, month: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Seleccionar mes</option>
                    <option value="Enero">Enero</option>
                    <option value="Febrero">Febrero</option>
                    <option value="Marzo">Marzo</option>
                    <option value="Abril">Abril</option>
                    <option value="Mayo">Mayo</option>
                    <option value="Junio">Junio</option>
                    <option value="Julio">Julio</option>
                    <option value="Agosto">Agosto</option>
                    <option value="Septiembre">Septiembre</option>
                    <option value="Octubre">Octubre</option>
                    <option value="Noviembre">Noviembre</option>
                    <option value="Diciembre">Diciembre</option>
                    <option value="Semestre 1">Semestre 1</option>
                    <option value="Semestre 2">Semestre 2</option>
                    <option value="Anual">Anual</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monto *
                </label>
                <input
                  type="text"
                  value={quotaEmail.amount}
                  onChange={handleAmountChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Solo números enteros (no puede empezar con 0)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número de Documento (Opcional)
                </label>
                <input
                  type="text"
                  value={quotaEmail.numberDocument}
                  onChange={(e) => setQuotaEmail({...quotaEmail, numberDocument: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Número de documento"
                />
              </div>

            </div>
          )}

          {/* Result Display */}
          {result && (
            <div className={`mt-4 p-4 rounded-lg flex items-center gap-2 ${
              result.success 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {result.success ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
              <span className="text-sm">{result.message}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <button
              onClick={resetForm}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              Limpiar
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              Cerrar
            </button>
            <button
              onClick={activeTab === 'normal' ? handleSendNormalEmail : handleSendQuotaEmail}
              disabled={isLoading || (activeTab === 'normal' ? !normalEmail.to || !normalEmail.subject || !normalEmail.message : !quotaEmail.to || !quotaEmail.subject || !quotaEmail.companyName || !quotaEmail.periodicity || !quotaEmail.month || !quotaEmail.amount)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Enviar {activeTab === 'quota' ? 'Cuota Social' : 'Email'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTestModal;